// 系譜・影響関係ビュー（時代別タイムラインカード）
// 旧 SVG 表示を廃止し、時代軸でグループ化したカードレイアウトに変更。
// 各著者カードをタップすると、代表作AI推薦・関連著者・系譜探索の 3 アクションが
// 下に展開される。アクションは keyword-search 画面に初期値を渡して遷移する。
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast } from '../ui.js';
import { fetchLineage, fetchAllNotesParallel } from '../api.js';
import { getAllBooks, upsertBooks, getAiCache, setAiCache, saveAiHistory, getAiHistoryByType } from '../cache.js';
import { renderHistorySection, bindHistoryHandlers, renderBookFilter, getFilterState, applyBookFilter, bindFilterCounter } from '../ui.js';

export async function showLineageScreen() {
  const histItems = await getAiHistoryByType('lineage');
  if (histItems.length > 0) {
    return renderEntry(histItems);
  }
  return runDirect();
}

async function renderEntry(histItems) {
  const books = await getAllBooks();
  const html = `
    ${screenHead('系譜・影響関係')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      読書履歴から思想家の影響ネットワークを抽出します。<br/>
      フィルターで対象を絞ることもできます。
    </p>
    ${renderBookFilter()}
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-network"/></svg>
      <span>新規に構築する</span>
    </button>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('lineage', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  bindFilterCounter(screen, books);
  screen.querySelector('#btn-go').addEventListener('click', () => runDirect(true, getFilterState(screen)));
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderResult(entry.payload),
    () => showLineageScreen()
  );
}

async function runDirect(forceNew = false, filterState = null) {
  let books = await getAllBooks();
  if (filterState) {
    const filtered = applyBookFilter(books, filterState);
    if (filtered.length >= 5) books = filtered;
  }
  if (books.length < 5) {
    showScreen('lineage', `${screenHead('系譜・影響関係')}<p class="empty">書籍が少なすぎます（${books.length} 冊）。5 冊以上 同期してからお試しください。</p>`);
    document.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
    return;
  }

  const cacheKey = `lineage:${books.length}:${books[0]?.updatedAt || ''}`;
  let data = forceNew ? null : await getAiCache(cacheKey);
  const fromCache = !!data;
  if (!data) {
    showLoading('系譜を構築中…');
    try {
      data = await fetchLineage(books);
      await setAiCache(cacheKey, data);
    } catch (e) {
      hideLoading();
      console.error('Lineage failed:', e);
      toast(`構築に失敗しました: ${e?.message || ''}`.slice(0, 80));
      return;
    } finally {
      hideLoading();
    }
  }
  if (!fromCache) {
    await saveAiHistory({
      type: 'lineage',
      title: `系譜（${data.nodes?.length || 0} 人 / ${data.edges?.length || 0} 関係）`,
      subtitle: data.nodes?.filter((n) => n.bookInList).slice(0, 3).map((n) => n.id).join(' / '),
      context: {},
      payload: data,
    });
  }
  renderResult(data);
}

export function renderFromHistory(entry) {
  renderResult(entry.payload);
}

// AI が返す era 文字列を「古代/中世/近世/18世紀/19世紀/20世紀/現代」のいずれかに正規化
function eraBucket(era) {
  const s = String(era || '').trim();
  if (!s) return '時代不明';
  if (/古代/.test(s)) return '古代';
  if (/中世/.test(s)) return '中世';
  if (/近世/.test(s)) return '近世';
  if (/現代/.test(s) && !/(\d)世紀/.test(s)) return '現代';
  // 「20世紀」「19世紀後半」などの世紀表記を優先（西暦数字より先に判定）
  const century = s.match(/(\d{1,2})\s*世紀/);
  if (century) {
    const c = parseInt(century[1], 10);
    if (c <= 5) return '古代';
    if (c <= 15) return '中世';
    if (c === 16 || c === 17) return '近世';
    if (c === 18) return '18世紀';
    if (c === 19) return '19世紀';
    if (c === 20) return '20世紀';
    return '現代';
  }
  // 西暦（3-4 桁）
  const year = s.match(/(\d{3,4})/);
  if (year) {
    const n = parseInt(year[1], 10);
    if (n < 500) return '古代';
    if (n < 1500) return '中世';
    if (n < 1700) return '近世';
    if (n < 1800) return '18世紀';
    if (n < 1900) return '19世紀';
    if (n < 2000) return '20世紀';
    return '現代';
  }
  return s;
}

const BUCKET_ORDER = ['古代', '中世', '近世', '18世紀', '19世紀', '20世紀', '現代', '時代不明'];

function groupByEra(nodes) {
  const groups = new Map();
  for (const n of nodes) {
    const bucket = eraBucket(n.era);
    if (!groups.has(bucket)) groups.set(bucket, []);
    groups.get(bucket).push(n);
  }
  // 各バケット内は「既読著者→未読著者」の順、それ以外は名前順
  for (const list of groups.values()) {
    list.sort((a, b) => {
      const ra = a.bookInList ? 0 : 1;
      const rb = b.bookInList ? 0 : 1;
      if (ra !== rb) return ra - rb;
      return (a.id || '').localeCompare(b.id || '', 'ja');
    });
  }
  return BUCKET_ORDER
    .filter((bucket) => groups.has(bucket))
    .map((bucket) => ({ bucket, nodes: groups.get(bucket) }));
}

// edge を著者ごとにグルーピング（このカードを開いた時にすぐ参照できるよう）
function edgesByNode(edges) {
  const map = new Map();
  for (const e of edges) {
    if (!map.has(e.from)) map.set(e.from, []);
    if (!map.has(e.to)) map.set(e.to, []);
    map.get(e.from).push({ ...e, direction: 'out' });
    map.get(e.to).push({ ...e, direction: 'in' });
  }
  return map;
}

function renderResult(data) {
  const nodes = data.nodes || [];
  const edges = data.edges || [];
  const groups = groupByEra(nodes);
  const edgeMap = edgesByNode(edges);

  const readCount = nodes.filter((n) => n.bookInList).length;
  const unreadCount = nodes.length - readCount;

  const html = `
    ${screenHead('系譜・影響関係')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      ${nodes.length} 人の思想家を時代別に並べました。<br/>
      既読 ${readCount} 人 / 未読 ${unreadCount} 人 · 影響関係 ${edges.length} 本
    </p>
    <div class="lineage-legend">
      <span class="lineage-legend__dot lineage-legend__dot--read"></span><span>既読著者</span>
      <span class="lineage-legend__dot"></span><span>未読著者</span>
    </div>
    <div class="lineage-timeline">
      ${groups
        .map(
          (g) => `
        <section class="lineage-era">
          <h3 class="lineage-era__title">${escapeHtml(g.bucket)}</h3>
          <ul class="lineage-era__list">
            ${g.nodes.map((n) => renderAuthorCard(n)).join('')}
          </ul>
        </section>
      `
        )
        .join('')}
    </div>

    <p class="section-label" style="margin-top:32px;">influence relations</p>
    <ul style="list-style:none;padding:0;margin:0 0 24px;">
      ${edges
        .map((e) => {
          const kindJa = e.kind === 'opposed' ? '⇄ 対立' : e.kind === 'extended' ? '→ 発展' : e.kind === 'cited' ? '→ 引用' : '→ 影響';
          return `
            <li style="display:flex;gap:10px;padding:8px 0;border-bottom:1px solid var(--line);align-items:baseline;font-size:13px;flex-wrap:wrap;">
              <span style="font-family:var(--font-serif);">${escapeHtml(e.from || '')}</span>
              <span style="font-family:var(--font-mono);font-size:10px;color:var(--mute);letter-spacing:0.08em;flex-shrink:0;">${escapeHtml(kindJa)}</span>
              <span style="font-family:var(--font-serif);">${escapeHtml(e.to || '')}</span>
            </li>
          `;
        })
        .join('')}
    </ul>
    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>最新の読書履歴から再構築する</span>
    </button>
  `;
  const screen = showScreen('lineage', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', () => runDirect(true));
  bindAuthorCards(screen, nodes, edgeMap);
}

function renderAuthorCard(n) {
  const klass = n.bookInList ? 'lineage-card lineage-card--read' : 'lineage-card';
  return `
    <li class="${klass}" data-author-id="${escapeHtml(n.id)}">
      <button type="button" class="lineage-card__head" data-toggle="1">
        <span class="lineage-card__dot"></span>
        <span class="lineage-card__name">${escapeHtml(n.id)}</span>
        <span class="lineage-card__meta">${escapeHtml(n.school || '')}</span>
        <span class="lineage-card__chevron" aria-hidden="true">＋</span>
      </button>
      <div class="lineage-card__panel" hidden></div>
    </li>
  `;
}

function bindAuthorCards(screen, nodes, edgeMap) {
  screen.querySelectorAll('[data-author-id]').forEach((li) => {
    const authorId = li.dataset.authorId;
    const node = nodes.find((n) => n.id === authorId);
    const head = li.querySelector('[data-toggle]');
    const panel = li.querySelector('.lineage-card__panel');
    head.addEventListener('click', () => {
      const isOpen = !panel.hidden;
      // 同じセクション内の他カードは閉じる
      li.closest('.lineage-era')?.querySelectorAll('.lineage-card__panel').forEach((p) => {
        if (p !== panel) {
          p.hidden = true;
          p.innerHTML = '';
        }
      });
      li.closest('.lineage-era')?.querySelectorAll('.lineage-card').forEach((c) => {
        if (c !== li) c.classList.remove('lineage-card--open');
      });
      if (isOpen) {
        panel.hidden = true;
        panel.innerHTML = '';
        li.classList.remove('lineage-card--open');
        head.querySelector('.lineage-card__chevron').textContent = '＋';
      } else {
        panel.hidden = false;
        panel.innerHTML = renderAuthorPanel(node, edgeMap.get(authorId) || []);
        li.classList.add('lineage-card--open');
        head.querySelector('.lineage-card__chevron').textContent = '−';
        bindPanelActions(panel, node, edgeMap.get(authorId) || []);
      }
    });
  });
}

function renderAuthorPanel(node, relations) {
  const inRel = relations.filter((r) => r.direction === 'in');  // この著者が影響を受けた
  const outRel = relations.filter((r) => r.direction === 'out'); // この著者が影響を与えた

  const relHtml = (list, label) =>
    list.length
      ? `<div style="margin-top:10px;">
           <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">${escapeHtml(label)}</p>
           <ul style="list-style:none;padding:0;margin:0;display:flex;flex-wrap:wrap;gap:6px;">
             ${list
               .map((r) => {
                 const other = r.direction === 'in' ? r.from : r.to;
                 const kindJa = r.kind === 'opposed' ? '⇄' : r.kind === 'extended' ? '↗' : r.kind === 'cited' ? '⌜⌝' : '→';
                 return `<li style="font-family:var(--font-serif);font-size:12.5px;color:var(--dim);padding:2px 6px;border:1px solid var(--line);background:var(--bg);">${escapeHtml(kindJa)} ${escapeHtml(other)}</li>`;
               })
               .join('')}
           </ul>
         </div>`
      : '';

  return `
    <div style="margin-top:10px;padding:12px 14px;border-top:1px solid var(--line);">
      <p style="margin:0;font-size:12px;color:var(--dim);line-height:1.7;">
        ${escapeHtml(node?.era || '')} · ${escapeHtml(node?.school || '')}${node?.bookInList ? ' · 読書記録に登録' : ''}
      </p>
      ${relHtml(inRel, 'influenced by')}
      ${relHtml(outRel, 'influenced')}
      <div style="display:flex;flex-direction:column;gap:6px;margin-top:14px;">
        <button class="btn btn--small btn--block btn--ink" data-act="represent" type="button">
          <svg class="icon"><use href="#i-sparkles"/></svg>
          <span>${node?.bookInList ? 'まだ読んでいない代表作を提案' : 'まず読むべき代表作を提案'}</span>
        </button>
        <button class="btn btn--small btn--block" data-act="related-authors" type="button">
          <svg class="icon"><use href="#i-network"/></svg>
          <span>思想的に近い別の著者の代表作</span>
        </button>
        <button class="btn btn--small btn--block btn--ghost" data-act="lineage-explore" type="button">
          <svg class="icon"><use href="#i-route"/></svg>
          <span>系譜（上流/下流）を辿る</span>
        </button>
      </div>
    </div>
  `;
}

function bindPanelActions(panel, node, relations) {
  if (!node) return;
  const inRel = relations.filter((r) => r.direction === 'in').map((r) => r.from);
  const outRel = relations.filter((r) => r.direction === 'out').map((r) => r.to);

  panel.querySelector('[data-act="represent"]').addEventListener('click', async () => {
    const intent = node.bookInList
      ? `${node.id} の代表作・主要著作のうち、まだ読んでいない（または読書記録の続きとして読むべき）日本語で読めるものを提案してください。既読書との関係や思想的位置づけにも触れてください。`
      : `${node.id} の代表作・主要著作で、まず初めに読むべき日本語で読めるものを 3-5 冊提案してください。${node.school ? `学派: ${node.school}。` : ''}${node.era ? `時代: ${node.era}。` : ''}難易度の異なるルートも示してください。`;
    window.history.pushState({ screen: 'keyword-search' }, '', '#keyword-search');
    const { showKeywordSearchScreen } = await import('./keyword-search.js');
    showKeywordSearchScreen({ initialKeyword: node.id, initialIntent: intent });
  });

  panel.querySelector('[data-act="related-authors"]').addEventListener('click', async () => {
    const intent = `${node.id} と思想的に近い、または同じ問題系を扱う別の著者の代表作を提案してください。${node.school ? `${node.school} の周辺、` : ''}${node.era ? `${node.era} 以降の発展も含めて、` : ''}既読書と接続する観点を示してください。`;
    window.history.pushState({ screen: 'keyword-search' }, '', '#keyword-search');
    const { showKeywordSearchScreen } = await import('./keyword-search.js');
    showKeywordSearchScreen({ initialKeyword: node.id, initialIntent: intent });
  });

  panel.querySelector('[data-act="lineage-explore"]').addEventListener('click', async () => {
    const upstream = inRel.length ? `この著者が影響を受けた思想家: ${inRel.join(', ')}。` : '';
    const downstream = outRel.length ? `この著者が影響を与えた思想家: ${outRel.join(', ')}。` : '';
    const intent = `${node.id} の系譜を辿りたいです。${upstream}${downstream}これらの上流（思想的源流）と下流（後継・批判的継承者）それぞれから、日本語で読める代表作を提案してください。`;
    window.history.pushState({ screen: 'keyword-search' }, '', '#keyword-search');
    const { showKeywordSearchScreen } = await import('./keyword-search.js');
    showKeywordSearchScreen({ initialKeyword: node.id, initialIntent: intent });
  });
}
